import { useT } from "@/i18n";
import { OnboardingStepView } from "@/types";
import {
  cn,
  IconCheckCircle,
  Skeleton,
  TextButton,
  tv,
  UnstyledGridList,
  UnstyledGridListItem,
  UnstyledLink,
  UnstyledText,
} from "@/ui";

// `auto-rows-fr` sizes every grid row to the tallest card's content, so all
// cards share one height (adapts to the longest description / translation
// instead of a fixed height that would clip). Cards `h-full` to fill the row.
const gridClassName =
  "grid gap-2.5 auto-rows-fr grid-cols-1 sm:grid-cols-[repeat(auto-fill,261px)]";

const itemVariants = tv({
  base: [
    "group flex flex-col gap-2 min-w-0",
    "p-4 min-h-[150px] w-full h-full",
    "bg-surface-1 border border-surface-2 rounded-md border-solid",
    "transition-all duration-150",
  ],
});

type OnboardingStepsGridProps = {
  ariaLabel: string;
  steps: OnboardingStepView[];
  /** When provided, incomplete cards show a "Mark as done" button. */
  onMarkDone?: (id: string) => void;
  isSaving?: boolean;
  isLoading?: boolean;
};

/**
 * Dedicated-page grid of onboarding steps. Renders ALL steps always: completed
 * ones get the muted visual + a checkmark, incomplete ones an optional
 * "Mark as done" button. Every card's title/description is a live link to its
 * setup screen (completed steps stay clickable). The card itself is not a link
 * so the "Mark as done" button can live inside it without nesting interactives.
 */
export const OnboardingStepsGrid = ({
  ariaLabel,
  steps,
  onMarkDone,
  isSaving,
  isLoading,
}: OnboardingStepsGridProps) => {
  if (isLoading) {
    return (
      <div className={gridClassName}>
        {steps.map((step) => (
          <Skeleton key={step.id} className="h-[150px] w-full rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <UnstyledGridList
      aria-label={ariaLabel}
      className={gridClassName}
      selectionMode="none"
      items={steps}
      layout="grid"
      dependencies={[onMarkDone, isSaving]}
    >
      {(step) => <OnboardingStepCard step={step} onMarkDone={onMarkDone} isSaving={isSaving} />}
    </UnstyledGridList>
  );
};

type OnboardingStepCardProps = {
  step: OnboardingStepView;
  onMarkDone?: (id: string) => void;
  isSaving?: boolean;
};

const OnboardingStepCard = ({ step, onMarkDone, isSaving }: OnboardingStepCardProps) => {
  const t = useT();
  const Icon = step.icon;
  const { isCompleted } = step;

  return (
    <UnstyledGridListItem id={step.id} textValue={t(step.title)} className={itemVariants()}>
      <div className="flex items-start justify-between w-full gap-2">
        <div
          aria-hidden="true"
          className={cn(
            "rounded-md size-10 flex items-center justify-center p-[3px] shrink-0",
            isCompleted ? "bg-black/5 text-base-tone-2" : "bg-brand-subtle-1 text-brand"
          )}
        >
          <Icon width={30} height={30} />
        </div>
        {isCompleted ? (
          <IconCheckCircle className="text-positive shrink-0" />
        ) : onMarkDone ? (
          <TextButton
            color="brand"
            size="md"
            onPress={() => onMarkDone(step.id)}
            isDisabled={isSaving}
            className="shrink-0"
          >
            {t("general.actions.markAsDone")}
          </TextButton>
        ) : null}
      </div>

      <UnstyledLink
        href={step.href}
        target={step.isExternal ? "_blank" : undefined}
        rel={step.isExternal ? "noreferrer" : undefined}
        className={cn(
          "flex flex-col gap-0.5 w-full rounded-md cursor-pointer",
          "outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2"
        )}
      >
        <UnstyledText
          className={cn("preset-label-xl", isCompleted ? "text-secondary" : "text-new-primary")}
        >
          {t(step.title)}
        </UnstyledText>
        <UnstyledText
          slot="description"
          className={cn("preset-body-lg", isCompleted ? "text-tertiary" : "text-secondary")}
        >
          {t(step.description)}
        </UnstyledText>
      </UnstyledLink>
    </UnstyledGridListItem>
  );
};
